import { jwtDecode } from 'jwt-decode';
import { useEffect, useState, type ReactNode } from 'react';

import ErrorPage from 'kerberus-components/src/Components/Error/ErrorPage';
import Fallback from 'kerberus-components/src/Components/Fallbacks/Fallback';

import { SsoToken } from '../Types/Auth';
import { useAuthStore } from './stores';

import type KeyCloak from 'keycloak-js';

const cloakInitializationTimeoutMs = 8000;

export default function Cloak({ children }: { readonly children: ReactNode }) {
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        let initialized = false;

        const setupCloak = () => {
            const cloak = (window as Window & { _cloak?: KeyCloak })._cloak;
            if (!cloak) {
                console.log('Keycloak setup failed');
                setError(true);
                return;
            }

            initialized = true;
            const decodedToken = cloak.token ? jwtDecode<SsoToken>(cloak.token) : null;
            useAuthStore.setState({
                decodedToken,
                hasNext: decodedToken?.realm_access.roles.includes('next'),
                keyCloak: cloak,
                subject: cloak.subject,
            });
            if ('__TAURI_INTERNALS__' in window && cloak.token && cloak.refreshToken && cloak.idToken) {
                const tauri = (
                    window as Window & {
                        __TAURI_INTERNALS__: {
                            invoke: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
                        };
                    }
                ).__TAURI_INTERNALS__;

                tauri
                    .invoke('plugin:renkiextra|keycloak_save_tokens', {
                        kcIdToken: cloak.idToken,
                        kcRefreshToken: cloak.refreshToken,
                        kcToken: cloak.token,
                    })
                    .then(() => {
                        console.log('Saved tokens');
                    })
                    .catch((error) => {
                        console.error('Failed to save tokens', error);
                    })
                    .finally(() => {
                        setReady(true);
                    });
            } else {
                setReady(true);
            }
        };

        const handleErr = () => {
            initialized = true;
            setError(true);
        };

        const initializationTimeoutId = window.setTimeout(() => {
            if (!initialized && !('_cloak' in window) && !('_cloakErr' in window)) {
                console.error('Keycloak initialization timed out');
                setError(true);
            }
        }, cloakInitializationTimeoutMs);

        if ('_cloak' in window) {
            setupCloak();
        } else if ('_cloakErr' in window) {
            setError(true);
        } else {
            document.addEventListener('cloakReady', setupCloak);
            document.addEventListener('cloakErr', handleErr);
            return () => {
                window.clearTimeout(initializationTimeoutId);
                document.removeEventListener('cloakReady', setupCloak);
                document.removeEventListener('cloakErr', handleErr);
            };
        }

        return () => {
            window.clearTimeout(initializationTimeoutId);
        };
    }, []);

    return error ? <ErrorPage /> : ready ? children : <Fallback />;
}
